import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { DataTable } from "primereact/datatable";
import { flushSync } from "react-dom";
import { Column } from "primereact/column";
import useDynamicRows from "../Utils/useDynamicRows";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import CrearPedidoModal from "./CrearPedidoModal";
import EditarPedidoModal from "./EditarPedidoModal";
import CargarListaDeEmpaqueModal from "./CargarListaDeEmpaqueModal";
import EditarListaDeEmpaqueModal from "./EditarListaDeEmpaqueModal";
import ResumenEstadosPedidos from "../Componentes/ResumenEstadoPedidos";
import { Formatofecha } from "../Componentes/Formatofecha";
import { Traductor } from "../Componentes/Traductor";
import BotonesLE from "../Componentes/BotonesLE";
import LoadingOverlay from "../Componentes/LoadingOverlay";
import EstadoPedidoCell from "../Componentes/EstadoPedidosCell.jsx";
import { fetchWithAuth, API_URL } from "../Componentes/fetchWithAuth.js";

import "../styles/App.css";
import "../styles/ListaPedidos.css";

//const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ListaPedidos = ({ language }) => {
  const [listaPedidos, setListaPedidos] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isCreateModalOpen, setCreateModalOpen] = useState(false); // Modal para Crear Pedido
  const [isEditModalOpen, setEditModalOpen] = useState(false); // Modal para Editar Pedido
  //const [selectedPedido, setSelectedPedido] = useState(null); // Pedido seleccionado para edición
  const [selectedPedidoId, setSelectedPedidoId] = useState(null);
  const [isUploadModalOpen, setUploadModalOpen] = useState(false);
  const [isEditEmpaqueModalOpen, setEditEmpaqueModalOpen] = useState(false);
  const [contenedoresPorPedido, setContenedoresPorPedido] = useState({});
  const [loading, setLoading] = useState(false);

  const toastCenter = useRef(null);

  const translations = useMemo(() => Traductor(language), [language]);

  const renderContenedores = useCallback(
    (rowData) => {
      const ids = contenedoresPorPedido[rowData.idpedido] || [];

      if (ids.length === 0) return null;

      const visibles = ids.slice(0, 2).join(", ");
      const ocultos = ids.length > 2 ? "..." : "";
      const tooltipText = ids.join(", ");

      return (
        <span title={tooltipText}>
          {visibles} {ocultos}
        </span>
      );
    },
    [contenedoresPorPedido],
  );

  const { columns } = translations;

  /*
    useEffect(() => {
        fetch(`${API_URL}/listapedidos`)
            .then((response) => response.json())
            .then((data) => {
                setListaPedidos(data);
                setFilteredListaPedidos(data);
            })
            .catch((err) => console.error(translations.erroralcargarpedido, err));
    }, []);
    */

  /***********Uso de toast************************************** */
  const showMessage = useCallback((severity, summary, detail) => {
    if (toastCenter.current) {
      toastCenter.current.show({ severity, summary, detail, life: 3000 });
    }
  }, []);

  useEffect(() => {
    const fetchPedidos = async () => {
      try {
        const pedidosRes = await fetchWithAuth(`${API_URL}/api/listapedidos`);
        const pedidosData = await pedidosRes.json();

        if (Array.isArray(pedidosData)) {
          setListaPedidos(pedidosData);

          const mapa = {};
          pedidosData.forEach((pedido) => {
            mapa[pedido.idpedido] = pedido.contenedores || [];
          });
          setContenedoresPorPedido(mapa);
        } else {
          const errorMsg =
            pedidosData?.error ||
            "Error: La respuesta del servidor no es válida o la sesión ha expirado.";
          console.error("fetchPedidos: data error", pedidosData);
          setListaPedidos([]);
          showMessage("error", translations.title_error, errorMsg);
        }
      } catch (err) {
        console.error(translations.erroralcargarpedido, err);
      }
    };

    fetchPedidos();
  }, [translations, showMessage]);

  const selectedPedido = useMemo(() => {
    if (!Array.isArray(listaPedidos)) return null;
    return listaPedidos.find((p) => p.idpedido === selectedPedidoId) || null;
  }, [selectedPedidoId, listaPedidos]);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const filteredListaPedidos = useMemo(() => {
    if (!Array.isArray(listaPedidos)) return [];
    if (!searchText) return listaPedidos;
    const value = searchText.toLowerCase();

    return listaPedidos.filter(
      (pedido) =>
        (pedido.sNombreEstadoPedido &&
          pedido.sNombreEstadoPedido.toLowerCase().includes(value)) ||
        (pedido.sNombreCliente &&
          pedido.sNombreCliente.toLowerCase().includes(value)) ||
        (pedido.sMarca && pedido.sMarca.toLowerCase().includes(value)) ||
        (pedido.sRegistroIngreso &&
          pedido.sRegistroIngreso.toLowerCase().includes(value)) ||
        (pedido.contenedores &&
          pedido.contenedores.some((c) => c.toLowerCase().includes(value))) ||
        (pedido.descripcion_mercancia &&
          pedido.descripcion_mercancia.toLowerCase().includes(value)) ||
        (pedido.idpedido && pedido.idpedido.toString().includes(value)),
    );
  }, [listaPedidos, searchText]);

  //Esta función realiza una solicitud fetch para obtener los datos actualizados desde el servidor y actualizar el estado de listaPedidos y filteredListaPedidos
  const cargarPedidos = useCallback(() => {
    flushSync(() => {
      setLoading(true);
    });
    fetchWithAuth(`${API_URL}/api/listapedidos`)
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setListaPedidos(data); // Actualiza la lista completa, el useMemo se encargará de re-filtrar
        } else {
          console.error("cargarPedidos: data is not an array", data);
          setListaPedidos([]);
          showMessage(
            "error",
            "Error",
            "No se pudieron cargar los pedidos. Por favor, inicie sesión nuevamente.",
          );
        }
      })
      .catch((err) => console.error("Error al cargar listapedidos", err))
      .finally(() => setLoading(false));
  }, [showMessage]); // Dependencia añadida para evitar warnings de ESLint

  const cambiarEstadoPedido = useCallback(
    async (idpedido) => {
      setLoading(true);
      try {
        // Construir la URL con el parámetro idpedido
        const response = await fetchWithAuth(
          `${API_URL}/api/cambiar-estado-pedido/${idpedido}`,
          {
            method: "PUT",
          },
        );

        // Analizar la respuesta JSON
        const data = await response.json();

        if (data.codigo === 0) {
          showMessage(
            "success",
            translations.text_Confirmacion,
            translations.actualizacion_exitosa,
          );
          cargarPedidos(); // Recarga la tabla de pedidos
        } else {
          showMessage(
            "error",
            translations.erroralactualizar,
            `${translations.erroralactualizar} : ${data.mensaje}`,
          );
        }
      } catch (err) {
        showMessage(
          "error",
          translations.title_error,
          `${translations.erroralactualizar} : ${err.message}`,
        );
      } finally {
        setLoading(false);
      }
    },
    [showMessage, translations, cargarPedidos],
  );

  const EliminarLEDelPedido = useCallback(
    async (idpedido) => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/api/eliminar-le-pedido/${idpedido}`,
          {
            method: "PUT",
          },
        );
        const data = await response.json();
        if (data.codigo === 0) {
          showMessage(
            "success",
            translations.listadeempaque_eliminada,
            translations.eliminar_le,
          );
          cargarPedidos(); // Recarga la tabla de pedidos
        } else {
          showMessage(
            "error",
            translations.error_eliminar_le,
            `${translations.error_eliminar_le}: ${data.mensaje}`,
          );
        }
      } catch (err) {
        showMessage(
          "error",
          translations.title_error,
          `${translations.error_eliminar_le}: ${err.message}`,
        );
      } finally {
        setLoading(false);
      }
    },
    [showMessage, translations, cargarPedidos],
  );

  const EliminarPedido = async (idpedido) => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/api/eliminar-pedido/${idpedido}`,
        {
          method: "PUT",
        },
      );
      const data = await response.json();
      if (data.codigo === 0) {
        showMessage(
          "success",
          translations.text_Confirmacion,
          translations.pedidoeliminado,
        );
        cargarPedidos(); // Recarga la tabla de pedidos
      } else {
        showMessage(
          "error",
          translations.title_error,
          `${translations.error_eliminar_pedido}: ${data.mensaje}`,
        );
      }
    } catch (err) {
      showMessage(
        "error",
        translations.title_error,
        `${translations.error_eliminar_pedido}: ${err.message}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const AnularPedido = async (idpedido) => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/api/anular-pedido/${idpedido}`,
        {
          method: "PUT",
        },
      );
      const data = await response.json();
      if (data.codigo === 0) {
        showMessage(
          "success",
          translations.text_Confirmacion,
          translations.pedidoanulado,
        );
        cargarPedidos(); // Recarga la tabla de pedidos
      } else {
        showMessage(
          "error",
          translations.title_error,
          `${translations.error_anular_pedido}: ${data.mensaje}`,
        );
      }
    } catch (err) {
      showMessage(
        "error",
        translations.title_error,
        `${translations.error_anular_pedido}: ${err.message}`,
      );
    } finally {
      setLoading(false);
    }
  };

  //Esta función se utiliza antes de abrir el modal de edición
  const handleEditPedido = () => {
    // Validar si el estado del pedido permite editar
    if (
      selectedPedidoId &&
      ["CREADO", "LE_OK", "LE_APROBADA"].includes(
        selectedPedido.sNombreEstadoPedido,
      )
    ) {
      setEditModalOpen(true); // Abre el modal si el estado lo permite
    } else {
      showMessage(
        "error",
        translations.title_error,
        translations.errorEditPedido,
      );
    }
  };

  //Esta función se utiliza cuando el usuario hace clic en el botón "Guardar" en el modal
  const handleUpdatePedido = (updatedPedido) => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/pedidos/${updatedPedido.idpedido}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updatedPedido),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((error) => {
            showMessage(
              "error",
              translations.title_error,
              translations.erroralactualizar,
            );
            throw new Error(translations.erroralactualizar);
          });
        }
        return response.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          // Actualiza la lista de pedidos
          setListaPedidos((prevPedidos) => {
            if (!Array.isArray(prevPedidos)) return data;
            return prevPedidos.map((pedido) =>
              pedido.idpedido === updatedPedido.idpedido
                ? updatedPedido
                : pedido,
            );
          });
        }
        setEditModalOpen(false); // Cierra el modal
        cargarPedidos();
        showMessage(
          "success",
          translations.text_Confirmacion,
          translations.actualizacion_exitosa,
        );
      })
      .catch((err) => {
        showMessage("error", translations.erroralactualizar, err.message);
      });
  };

  const EditarListaDeEmpaque = (updatedEmpaque) => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/editar-lista-empaque`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        idpedido: updatedEmpaque.idpedido,
        data: updatedEmpaque,
      }), // Enviar el ID del pedido y los datos editados
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((error) => {
            showMessage(
              "error",
              translations.erroralactualizar,
              translations.le_noactualizada,
            );
            throw new Error(translations.erroralactualizar);
          });
        }
        return response.json();
      })
      .then((data) => {
        showMessage(
          "success",
          translations.actualizacion_exitosa,
          translations.le_actualizada,
        );
        cargarPedidos(); // Recargar la lista de pedidos si es necesario
        setEditEmpaqueModalOpen(false); // Cerrar el modal después de actualizar
      })
      .catch((err) => {
        showMessage("error", translations.erroralactualizar, err.message);
      });
  };

  const handleCargar = useCallback((pedido) => {
    //setSelectedPedido(pedido);
    setSelectedPedidoId(pedido.idpedido);
    setUploadModalOpen(true);
  }, []);

  const handleVer = useCallback((pedido) => {
    //setSelectedPedido(pedido);
    setSelectedPedidoId(pedido.idpedido);
    setEditEmpaqueModalOpen(true);
  }, []);

  const handleAprobar = useCallback(
    (pedido) => {
      cambiarEstadoPedido(pedido.idpedido);
    },
    [cambiarEstadoPedido],
  );

  const handleEliminarLE = useCallback(
    (idpedido) => {
      EliminarLEDelPedido(idpedido);
    },
    [EliminarLEDelPedido],
  );

  /*
    const renderBotonesLE = useCallback((rowData) => {
            
            const isSelected = rowData.idpedido === selectedPedidoId;
            if (!isSelected) return null;
 
            console.log('Render fila LE_Button:', rowData.idpedido);
 
            return (
                <BotonesLE
                    rowData={rowData}
                    translations={translations}
                    showMessage={showMessage}
                    onAprobar={handleAprobar}
                    onCargar={handleCargar}
                    onVer={handleVer}
                    onEliminar={(pedido) => handleEliminarLE(pedido.idpedido)}
                />
            );
        }, [translations, showMessage, handleAprobar, handleCargar, handleVer, handleEliminarLE, selectedPedidoId]);
*/

  const renderBotonesLE = useCallback(
    (rowData) => {
      const isSelected = rowData.idpedido === selectedPedidoId;
      if (!isSelected) return null;

      const result = (
        <BotonesLE
          rowData={rowData}
          translations={translations}
          showMessage={showMessage}
          onAprobar={handleAprobar}
          onCargar={handleCargar}
          onVer={handleVer}
          onEliminar={(pedido) => handleEliminarLE(pedido.idpedido)}
        />
      );

      return result;
    },
    [
      translations,
      showMessage,
      handleAprobar,
      handleCargar,
      handleVer,
      handleEliminarLE,
      selectedPedidoId,
    ],
  );

  const columnElements = useMemo(() => {
    const cols = columns.map((col, index) => (
      <Column
        key={index}
        field={col.field}
        header={col.header}
        style={{
          width: col.width || "auto",
          minWidth: col.width || "auto",
          maxWidth: col.width || "auto", // [EXPERT_FIX]: Restored maxWidth to prevent force-expansion
          whiteSpace: "nowrap",
        }}
        body={(rowData) => {
          let content = rowData[col.field];

          if (col.field === "sNombreEstadoPedido") {
            content = <EstadoPedidoCell estado={rowData.sNombreEstadoPedido} />;
          } else if (
            [
              "fecha_pedido",
              "fecha_llegada_a_bodega",
              "fecha_llegada_a_bodega_estimada",
            ].includes(col.field)
          ) {
            const date = rowData[col.field];
            content = date ? Formatofecha(date) : "";
          } else if (["peso_bruto", "dMetrosCubicos"].includes(col.field)) {
            const value = parseFloat(rowData[col.field]);
            content = isNaN(value) ? "" : value.toFixed(2);
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span id={`pedido-celda-${rowData.idpedido}-${col.field}`}>
                {content}
              </span>
              {col.field === "idpedido" && rowData.hasRegistro && (
                <i className="pi pi-check-circle" style={{ color: 'var(--green-500)', fontSize: '1rem' }} title="Mercancía con registro de ingreso"></i>
              )}
            </div>
          );
        }}
        sortable
      />
    ));

    cols.push(
      <Column
        key="ctn"
        header="Ctn"
        body={renderContenedores}
        exportable={false}
        style={{ minWidth: "40px", maxWidth: "40px" }}
      />,
    );
    cols.push(
      <Column
        key="actions"
        header={translations.LE_Button}
        exportable={false}
        body={renderBotonesLE}
        style={{ minWidth: "150px", maxWidth: "150px" }}
      />,
    );

    return cols;
  }, [columns, translations, renderContenedores, renderBotonesLE]);

  // [EXPERT_FIX]: Restauración de funciones de confirmación y eventos
  const confirmDeletePedido = (pedido) => {
    confirmDialog({
      message: translations.dialog_mje_elim_ped,
      header: translations.text_Confirmacion,
      icon: "pi pi-question-circle",
      acceptClassName: "p-button-danger",
      className: "confirm-dialog-small",
      style: { width: "450px" },
      accept: () => EliminarPedido(pedido.idpedido),
      reject: () =>
        showMessage(
          "info",
          translations.accioncancelada,
          translations.accioncancelada,
        ),
    });
  };

  const confirmAnularPedido = (pedido) => {
    confirmDialog({
      message: translations.dialog_mje_anular_ped,
      header: translations.text_Confirmacion,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-warning",
      className: "confirm-dialog-small",
      style: { width: "450px" },
      accept: () => AnularPedido(pedido.idpedido),
      reject: () =>
        showMessage(
          "info",
          translations.accioncancelada,
          translations.accioncancelada,
        ),
    });
  };

  // [EXPERT_FEATURE]: Cálculo dinámico de filas visibles
  const dynamicRows = useDynamicRows({
    rowHeight: 50, // [EXPERT_FIX]: Ajustado a 50px para compensar aumento de altura de fila
    fixedOffset: 290, // [EXPERT_FIX]: Reducido 100px para mostrar 2 filas más (50px * 2)
    minRows: 5, // Mínimo absoluto
    defaultRows: 10, // Valor inicial
  });

  const dataTable = useMemo(
    () => (
      <DataTable
        value={filteredListaPedidos}
        virtualScrollerOptions={{ itemSize: 50 }}
        dataKey="idpedido"
        selectionMode="single"
        selection={
          Array.isArray(listaPedidos)
            ? listaPedidos.find((p) => p.idpedido === selectedPedidoId)
            : null
        }
        onSelectionChange={(e) => setSelectedPedidoId(e.value?.idpedido)}
        paginator
        rows={dynamicRows}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} pedidos"
        rowClassName={(rowData) => {
          return rowData.idpedido === selectedPedidoId
            ? "fila-seleccionada"
            : "";
        }}
        emptyMessage={translations.noPedidos}
      >
        {columnElements}
      </DataTable>
    ),
    [
      filteredListaPedidos,
      selectedPedidoId,
      columnElements,
      listaPedidos,
      setSelectedPedidoId,
      dynamicRows,
      translations,
    ],
  );

  return (
    <>
      <Toast ref={toastCenter} position="top-center" baseZIndex={9999} />
      <ConfirmDialog
        style={{ width: "450px" }}
        className="confirm-dialog-small"
      />
      {loading && <LoadingOverlay message="Procesando..." />}
      <div className="contenedorprincipal pagina-pedidos">
        {/* [EXPERT_FIX]: Wrapper absoluto para resumen. Top 0px = alineado con Título */}
        <div
          style={{
            position: "absolute",
            top: "15px",
            right: "20px",
            userSelect: "none",
            zIndex: 100,
          }}
        >
          <ResumenEstadosPedidos data={filteredListaPedidos} />
        </div>

        <h1 className="titulo">{translations.GestionPedidos}</h1>
        <div className="botonessuperiores">
          {/* Botones de acción */}
          <Button
            id="btn-abrir-crear-pedido"
            label={translations.createButton}
            icon="pi pi-plus"
            className="p-button-success"
            onClick={() => setCreateModalOpen(true)}
          />
          <Button
            label={translations.editButton}
            icon="pi pi-pencil"
            className="p-button-warning"
            onClick={handleEditPedido}
            disabled={!selectedPedido}
          />
          <Button
            label={translations.deleteButton}
            icon="pi pi-delete-left"
            className="p-button-danger"
            disabled={!selectedPedido}
            onClick={() => {
              if (
                !selectedPedido ||
                !["CREADO"].includes(selectedPedido.sNombreEstadoPedido)
              ) {
                showMessage(
                  "error",
                  translations.estadoincorrecto,
                  translations.ped_creado_borrar_le,
                );
                return;
              }
              confirmDeletePedido(selectedPedido);
            }}
          />
          <Button
            label={translations.anularButton}
            icon="pi pi-ban"
            className="p-button-secondary"
            disabled={!selectedPedido}
            onClick={() => confirmAnularPedido(selectedPedido)}
          />
        </div>

        {isCreateModalOpen && (
          <CrearPedidoModal
            isOpen={isCreateModalOpen}
            onClose={() => setCreateModalOpen(false)}
            language={language}
            onSave={cargarPedidos}
          />
        )}

        {isEditModalOpen && selectedPedido && (
          <EditarPedidoModal
            isOpen={isEditModalOpen}
            onClose={() => setEditModalOpen(false)}
            pedido={selectedPedido}
            language={language}
            onSave={handleUpdatePedido}
          />
        )}

        {isUploadModalOpen && (
          <CargarListaDeEmpaqueModal
            isOpen={isUploadModalOpen}
            onClose={() => setUploadModalOpen(false)}
            pedido={selectedPedido}
            language={language}
            onSaveSuccess={cargarPedidos} //permite refrescar la lista de pedidos
          />
        )}

        {isEditEmpaqueModalOpen && selectedPedido && (
          <EditarListaDeEmpaqueModal
            isOpen={isEditEmpaqueModalOpen}
            pedido={selectedPedido}
            language={language}
            onSaveSuccess={EditarListaDeEmpaque} // ✅ Esto actualiza la lista de pedidos
            onClose={() => setEditEmpaqueModalOpen(false)}
          />
        )}

        <div style={{ margin: "20px 0 2px 0", textAlign: "center" }}>
          <input
            type="text"
            placeholder={translations.searchPlaceholder}
            value={searchText}
            onChange={handleSearch}
            style={{ padding: "5px", width: "200px" }}
          />
        </div>

        {/* DataTable memoizada para que el overlay sea instantáneo */}
        {dataTable}

        {/* Espaciador visual al final para que el último registro no quede pegado */}
        <div style={{ height: "10px" }}></div>
      </div>
    </>
  );
};

export default ListaPedidos;
